(() => {
  const rootSelector = '[data-electrix-cro-filtering-root]';
  const navSelector = '#ElectrixCollectionNav';
  const CONTENT_GAP = 24; // breathing room between the sticky header and the results
  const NAV_TOP_MARGIN = 8; // breathing room above an entry lifted to the top of the sidebar
  const linkSelector = '[data-electrix-collection-link], [data-electrix-cro-sort-link], .electrix-collection-showcase a[href*="/collections/"]';

  const getRoot = () => document.querySelector(rootSelector);

  const shouldHandleLink = (event, link) => {
    if (!link || event.defaultPrevented) return false;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;

    const rawHref = link.getAttribute('href');
    if (!rawHref || rawHref.trim().startsWith('#')) return false;

    const url = new URL(link.href, window.location.href);
    return url.origin === window.location.origin && url.pathname.includes('/collections/');
  };

  const swapCollectionBanner = (parsedHTML) => {
    const currentBanner = document.querySelector('.collection-banner-section');
    const nextBanner = parsedHTML.querySelector('.collection-banner-section');

    if (!currentBanner || !nextBanner) return;
    currentBanner.innerHTML = nextBanner.innerHTML;
  };

  // Land on the results rather than the banner, so the products fill the screen instead of
  // sitting below a screenful of title. On mobile the filter bar sits above them, so aim there.
  const getScrollTarget = () => {
    const mobileFilter = document.querySelector('.electrix-cro-mobile-filter');
    if (mobileFilter && mobileFilter.offsetParent !== null) return mobileFilter;

    return document.getElementById('ProductGridContainer')
      || document.querySelector('.collection-banner-section')
      || getRoot();
  };

  const scrollToContentTop = () => {
    const target = getScrollTarget();
    if (!target) return;

    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const top = Math.max(target.getBoundingClientRect().top + window.scrollY - headerHeight - CONTENT_GAP, 0);

    if (window.scrollY > top) window.scrollTo({ top, behavior: 'auto' });
  };

  const getNav = () => document.querySelector(navSelector);

  const revealActiveNavItem = (nav) => {
    if (!nav || nav.scrollHeight <= nav.clientHeight) return;

    const active = Array.from(nav.querySelectorAll('.electrix-category-nav__link.is-current'))
      .find((link) => link.offsetParent !== null);
    if (!active) return;

    const navRect = nav.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    if (activeRect.top >= navRect.top && activeRect.bottom <= navRect.bottom) return;

    // Scroll the sidebar only — scrollIntoView would move the window too.
    nav.scrollTop += activeRect.top - navRect.top - navRect.height / 3;
  };

  // Scroll the sidebar so `el` sits `margin` px below its top edge. Measured in content space,
  // so a sticky sidebar that is mid-transition does not throw the maths off.
  const alignInNav = (nav, el, margin) => {
    const contentTop = el.getBoundingClientRect().top - nav.getBoundingClientRect().top + nav.scrollTop;
    nav.scrollTop = contentTop - margin;
  };

  // Used when the sidebar had no say in the navigation (a card in the results, sorting, page load):
  // show the whole model group, then make sure the active row itself made it into view.
  const alignActiveNavGroup = (nav) => {
    if (!nav) return;

    const active = nav.querySelector('.electrix-category-nav__link.is-current');
    if (!active || active.offsetParent === null) return;

    alignInNav(nav, active.closest('.electrix-category-nav__item') || active, NAV_TOP_MARGIN);
    revealActiveNavItem(nav);
  };

  // Remember where the clicked entry sits inside the sidebar so it can be put
  // back in place, even though the swap rebuilds the whole sidebar.
  const captureNavState = (link) => {
    const nav = getNav();
    if (!nav) return null;

    const anchored = link && nav.contains(link);

    return {
      scrollTop: nav.scrollTop,
      href: anchored ? link.getAttribute('href') : null,
      // links that open a list of categories live inside a <summary>
      expandable: anchored && link.closest('summary') !== null,
      offset: anchored ? link.getBoundingClientRect().top - nav.getBoundingClientRect().top : 0
    };
  };

  // The sticky header resizes the sidebar as the page settles, which can clip the category that
  // was just picked. Nudge it back by the exact overflow — small enough to go unnoticed.
  const keepActiveNavItemInView = () => {
    const nav = getNav();
    if (!nav) return;

    const link = nav.querySelector('.electrix-category-nav__link.is-current');
    if (!link || link.offsetParent === null) return;

    const navRect = nav.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();

    if (linkRect.top < navRect.top) nav.scrollTop -= navRect.top - linkRect.top;
    else if (linkRect.bottom > navRect.bottom) nav.scrollTop += linkRect.bottom - navRect.bottom;
  };

  const restoreNavScroll = (navState) => {
    const nav = getNav();
    if (!nav || !navState) return;

    nav.scrollTop = navState.scrollTop;

    if (!navState.href) {
      alignActiveNavGroup(nav);
      return;
    }

    const link = Array.from(nav.querySelectorAll('.electrix-category-nav__link[href]'))
      .find((el) => el.getAttribute('href') === navState.href && el.offsetParent !== null);
    if (!link) return;

    // A model just opened its category list, so lift it to the top to show the whole list.
    // A category keeps the exact spot it was clicked in.
    alignInNav(nav, link, navState.expandable ? NAV_TOP_MARGIN : navState.offset);
  };

  const swapCollectionContent = (html, url, link) => {
    const currentRoot = getRoot();
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = parsedHTML.querySelector(rootSelector);

    if (!currentRoot || !nextRoot) {
      window.location.href = url.toString();
      return;
    }

    const nextTitle = parsedHTML.querySelector('title');
    if (nextTitle) document.title = nextTitle.textContent;

    const navState = captureNavState(link);

    swapCollectionBanner(parsedHTML);
    currentRoot.innerHTML = nextRoot.innerHTML;
    document.querySelectorAll('.electrix-cro-filter-drawer').forEach((drawer) => {
      if (!getRoot()?.contains(drawer)) drawer.remove();
    });
    document.body.classList.remove('has-modal-open', 'has-modal-opening');
    // Restore before scrolling the window: the sticky header changes the sidebar height,
    // so measuring here keeps the same geometry the link was clicked in.
    restoreNavScroll(navState);
    scrollToContentTop();
    window.setTimeout(keepActiveNavItemInView, 600); // after the sticky header transition
    window.history.pushState({}, '', url.toString());
    document.dispatchEvent(new CustomEvent('collection:reloaded'));
  };

  const navigateToCollection = async (url, link) => {
    const root = getRoot();
    if (!root) {
      window.location.href = url.toString();
      return;
    }

    root.classList.add('is-loading');

    try {
      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);

      const html = await response.text();
      swapCollectionContent(html, url, link);
    }
    catch (error) {
      window.location.href = url.toString();
    }
    finally {
      const updatedRoot = getRoot();
      if (updatedRoot) updatedRoot.classList.remove('is-loading');
    }
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest(linkSelector);
    if (!shouldHandleLink(event, link)) return;

    event.preventDefault();
    navigateToCollection(new URL(link.href), link);
  });

  window.addEventListener('popstate', () => {
    window.location.reload();
  });

  const alignNavOnLoad = () => alignActiveNavGroup(getNav());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', alignNavOnLoad);
  }
  else {
    alignNavOnLoad();
  }
})();
